/**
 * TodaysWorkplanWidget — shows the signed-in worker's workplan instance(s)
 * for today on their Home dashboard.
 *
 * Loads from /api/workplans/today. The endpoint lazily generates a fresh
 * instance per (worker, plan, calendar-day) the first time it's called each
 * day, so this widget is the trigger that wakes the daily routine.
 *
 * Designed to be dropped into the Home page as <TodaysWorkplanWidget />.
 * Self-contained — no shared state required, no props.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar, MapPin, ChevronDown, ChevronUp, Paperclip, FileText, Image as ImageIcon, Link as LinkIcon, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.__BACKEND_URL__}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function TodaysWorkplanWidget() {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // {instanceId: bool}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/workplans/today`, { headers: authHeaders() });
      setInstances(r.data.instances || []);
      // Auto-expand the first card so today's tasks are immediately visible.
      if ((r.data.instances || []).length > 0) {
        setExpanded({ [r.data.instances[0].id]: true });
      }
    } catch (err) {
      // Silent: a worker with no assigned workplans is the common case.
      console.warn('Workplan widget: no instances or fetch error', err);
      setInstances([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleTask = async (instance, taskId) => {
    const updated = (instance.tasks || []).map((t) =>
      t.id === taskId
        ? { ...t, checked: !t.checked, completed_at: !t.checked ? new Date().toISOString() : null }
        : t,
    );
    // Optimistic update.
    setInstances((prev) => prev.map((i) => (i.id === instance.id ? { ...i, tasks: updated } : i)));
    try {
      await axios.put(`${API}/workplans/instances/${instance.id}`, { tasks: updated }, { headers: authHeaders() });
    } catch (err) {
      console.error(err);
      toast.error('Could not update — reloading');
      load();
    }
  };

  const markComplete = async (instance) => {
    try {
      await axios.put(
        `${API}/workplans/instances/${instance.id}`,
        { completed: true, completed_at: new Date().toISOString() },
        { headers: authHeaders() },
      );
      toast.success('Workplan completed for today');
      load();
    } catch (err) {
      console.error(err);
      toast.error('Failed to mark complete');
    }
  };

  if (loading) return null;
  if (instances.length === 0) return null; // hide widget entirely if no workplans

  return (
    <Card className="p-4 mb-4" data-testid="todays-workplan-widget">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-lg flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-600" />
          Today's Workplan{instances.length > 1 ? 's' : ''}
        </h2>
        <span className="text-xs text-slate-500">{new Date().toLocaleDateString()}</span>
      </div>

      <div className="space-y-3">
        {instances.map((inst) => {
          const tasks = inst.tasks || [];
          const done = tasks.filter((t) => t.checked).length;
          const total = tasks.length;
          const pct = total > 0 ? Math.round((done / total) * 100) : 0;
          const isOpen = !!expanded[inst.id];

          return (
            <div
              key={inst.id}
              className="border rounded-lg overflow-hidden"
              data-testid={`workplan-instance-${inst.id}`}
            >
              <button
                type="button"
                onClick={() => setExpanded((p) => ({ ...p, [inst.id]: !isOpen }))}
                className="w-full p-3 flex items-start justify-between text-left hover:bg-slate-50 dark:hover:bg-slate-800/50"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{inst.title}</p>
                  {inst.location && (
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3" /> {inst.location}
                    </p>
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs text-slate-500 whitespace-nowrap">
                      {done}/{total}
                    </span>
                  </div>
                </div>
                <span className="ml-2 text-slate-400">
                  {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </span>
              </button>

              {isOpen && (
                <div className="border-t p-3 space-y-3 bg-slate-50/50 dark:bg-slate-900/30">
                  {inst.description && (
                    <p className="text-sm text-slate-600 dark:text-slate-300">{inst.description}</p>
                  )}

                  {tasks.length > 0 && (
                    <div className="space-y-1.5">
                      {tasks.map((t) => (
                        <label
                          key={t.id}
                          className="flex items-start gap-2 cursor-pointer hover:bg-white dark:hover:bg-slate-800 rounded p-1"
                        >
                          <Checkbox
                            checked={!!t.checked}
                            onCheckedChange={() => toggleTask(inst, t.id)}
                            className="mt-0.5"
                            data-testid={`workplan-task-${t.id}`}
                          />
                          <span className={`text-sm flex-1 ${t.checked ? 'line-through text-slate-400' : ''}`}>
                            {t.text}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}

                  {(inst.attachments || []).length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5 flex items-center gap-1">
                        <Paperclip className="w-3 h-3" /> Documents & Links
                      </p>
                      <div className="space-y-1">
                        {(inst.attachments || []).map((a) => {
                          const icon =
                            a.type === 'pdf' ? <FileText className="w-4 h-4" /> :
                            a.type === 'image' ? <ImageIcon className="w-4 h-4" /> :
                            a.type === 'link' ? <LinkIcon className="w-4 h-4" /> :
                            <FileText className="w-4 h-4" />;
                          // Inline images can render right here; otherwise show as a link.
                          if (a.type === 'image' && a.data_base64) {
                            return (
                              <details key={a.id} className="text-sm">
                                <summary className="cursor-pointer flex items-center gap-1.5 text-blue-600 hover:underline">
                                  {icon} {a.name}
                                </summary>
                                <img
                                  src={`data:${a.mime || 'image/png'};base64,${a.data_base64}`}
                                  alt={a.name}
                                  className="mt-2 max-w-full h-auto rounded border"
                                />
                              </details>
                            );
                          }
                          if (a.url) {
                            return (
                              <a
                                key={a.id}
                                href={a.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm flex items-center gap-1.5 text-blue-600 hover:underline"
                              >
                                {icon} {a.name} <ExternalLink className="w-3 h-3" />
                              </a>
                            );
                          }
                          // PDF / doc as inline base64 — open in new tab via data URL.
                          if (a.data_base64) {
                            return (
                              <a
                                key={a.id}
                                href={`data:${a.mime || 'application/octet-stream'};base64,${a.data_base64}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                download={a.name}
                                className="text-sm flex items-center gap-1.5 text-blue-600 hover:underline"
                              >
                                {icon} {a.name}
                              </a>
                            );
                          }
                          return (
                            <span key={a.id} className="text-sm flex items-center gap-1.5 text-slate-500">
                              {icon} {a.name}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {!inst.completed && total > 0 && done === total && (
                    <Button size="sm" className="w-full" onClick={() => markComplete(inst)} data-testid={`workplan-complete-${inst.id}`}>
                      Mark workplan complete for today
                    </Button>
                  )}
                  {inst.completed && (
                    <div className="text-sm text-emerald-600 dark:text-emerald-400 font-medium text-center">
                      ✓ Completed for today
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
